import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  TextField,
  Typography,
} from "@mui/material";
import { setSnackbar } from "../../snackbar/api/snackbar";
import { inviteInternalUser } from "../api/actions";
import { ROLES } from "../accessRoles";

const EDITABLE_ROLES = ROLES.filter((r) => r.key !== "isSuperUser");
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const EMPTY = { firstName: "", lastName: "", email: "", ...Object.fromEntries(EDITABLE_ROLES.map((r) => [r.key, false])) };

function InvitePersonDialog({ open, onClose, inviteInternalUser, setSnackbar }) {
  const [form, setForm] = useState(EMPTY);
  const [touched, setTouched] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open) { setForm(EMPTY); setTouched(false); setError(null); }
  }, [open]);

  const missing = !form.firstName.trim() || !form.lastName.trim() || !form.email.trim();
  const badEmail = form.email.trim() !== "" && !EMAIL_RE.test(form.email.trim());

  const submit = async (e) => {
    e.preventDefault();
    setTouched(true);
    if (missing || badEmail) return;
    setSaving(true);
    setError(null);
    try {
      const roles = Object.fromEntries(EDITABLE_ROLES.map((r) => [r.key, form[r.key] ? 1 : 0]));
      const email = form.email.trim();
      const { rolesSaved } = await inviteInternalUser({ firstName: form.firstName.trim(), lastName: form.lastName.trim(), email, roles });
      if (rolesSaved) {
        setSnackbar(true, "success", `Invite sent to ${email}`);
      } else {
        setSnackbar(true, "warning", `Invite sent to ${email}, but the roles didn't save. Open them in the list to set roles.`);
      }
      onClose();
    } catch (err) {
      const code = err?.response?.data?.ERROR;
      setError(code === "EMAIL_ALREADY_IN_USE" ? "Someone already uses that email." : "The invite didn't go out. Try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="sm" component="form" onSubmit={submit} noValidate>
      <DialogTitle>Invite a person</DialogTitle>
      <DialogContent sx={{ display: "grid", gap: 2, pt: "8px !important" }}>
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 }}>
          <TextField label="First name" value={form.firstName} onChange={(e) => setForm({ ...form, firstName: e.target.value })}
            required error={touched && !form.firstName.trim()} autoFocus />
          <TextField label="Last name" value={form.lastName} onChange={(e) => setForm({ ...form, lastName: e.target.value })}
            required error={touched && !form.lastName.trim()} />
        </Box>
        <TextField label="Email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
          required error={(touched && !form.email.trim()) || badEmail}
          helperText={badEmail ? "Enter a valid email." : "They'll get an email with a link to set their password."} />
        <Box sx={{ display: "grid", gap: 1 }}>
          <Typography variant="overline" color="text.secondary" sx={{ lineHeight: 1.5 }}>Roles</Typography>
          {EDITABLE_ROLES.map((role) => (
            <FormControlLabel
              key={role.key}
              sx={{ alignItems: "flex-start", m: 0 }}
              control={<Checkbox checked={form[role.key]} onChange={(e) => setForm({ ...form, [role.key]: e.target.checked })} sx={{ pt: 0.25 }} />}
              label={
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>{role.name}</Typography>
                  <Typography variant="caption" color="text.secondary">{role.opens}</Typography>
                </Box>
              }
            />
          ))}
        </Box>
        {touched && missing && <Alert severity="error">Fill in first name, last name and email.</Alert>}
        {error && <Alert severity="error">{error}</Alert>}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={saving} sx={{ textTransform: "none" }}>Cancel</Button>
        <Button type="submit" variant="contained" color="secondary" disabled={saving} sx={{ textTransform: "none" }}>
          {saving ? "Sending…" : "Send invite"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default connect(null, { inviteInternalUser, setSnackbar })(InvitePersonDialog);
